import apiClient, { transformResponse } from './client';
import { API_ENDPOINTS } from '@/utils/constants';
import type { ApiResponse } from '@/types';
import type {
  DashboardData,
  PublicProfileData,
  InsightsSummary,
  DashboardProfile,
  ApiRelease,
  ApiEvent,
  ApiLink,
  ApiContact,
  UpdateProfileInput,
  CreateReleaseInput,
  UpdateReleaseInput,
  CreateEventInput,
  UpdateEventInput,
  CreateLinkInput,
  UpdateLinkInput,
  CreateContactInput,
  UpdateContactInput,
  CreateFeedbackInput,
} from '@/types/dashboard';

type Message = { message: string };

const byId = (base: string, id: string) => `${base}/${id}`;

export const dashboardApi = {
  // --- reads ---
  getDashboard: async () => {
    const res = await apiClient.get<ApiResponse<DashboardData>>(
      API_ENDPOINTS.USER.DASHBOARD,
    );
    return transformResponse(res);
  },

  getPublicProfile: async (handle: string) => {
    const res = await apiClient.get<ApiResponse<PublicProfileData>>(
      API_ENDPOINTS.PROFILES(handle),
    );
    return transformResponse(res);
  },

  getInsights: async () => {
    const res = await apiClient.get<ApiResponse<InsightsSummary>>(
      API_ENDPOINTS.USER.INSIGHTS,
    );
    return transformResponse(res);
  },

  // --- profile / account ---
  updateProfile: async (data: UpdateProfileInput) => {
    const res = await apiClient.patch<ApiResponse<DashboardProfile>>(
      API_ENDPOINTS.USER.ME,
      data,
    );
    return transformResponse(res);
  },

  // Multipart upload; the server re-encodes and stores it on S3, points the
  // profile's avatar at the new URL, and flips useAvatarPreset off.
  uploadAvatar: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post<ApiResponse<DashboardProfile>>(
      API_ENDPOINTS.USER.AVATAR,
      form,
    );
    return transformResponse(res);
  },

  // Clears the photo (deleting an uploaded object from S3 server-side) and
  // flips useAvatarPreset on, so the profile falls back to the preset.
  removeAvatar: async () => {
    const res = await apiClient.delete<ApiResponse<DashboardProfile>>(
      API_ENDPOINTS.USER.AVATAR,
    );
    return transformResponse(res);
  },

  deleteAccount: async () => {
    const res = await apiClient.delete<ApiResponse<Message>>(
      API_ENDPOINTS.USER.ME,
    );
    return transformResponse(res);
  },

  // --- releases ---
  // Multipart upload; the server re-encodes the image and stores it on S3,
  // returning just the public URL — it lands in the DB via the release
  // create/update payload's coverImage, not here.
  uploadCover: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post<ApiResponse<{ url: string }>>(
      API_ENDPOINTS.USER.COVER,
      form,
    );
    return transformResponse(res);
  },
  discardCover: async (url: string) => {
    const res = await apiClient.delete<ApiResponse<Message>>(
      API_ENDPOINTS.USER.COVER,
      { data: { url } },
    );
    return transformResponse(res);
  },
  createRelease: async (data: CreateReleaseInput) => {
    const res = await apiClient.post<ApiResponse<ApiRelease>>(
      API_ENDPOINTS.RELEASES,
      data,
    );
    return transformResponse(res);
  },
  updateRelease: async (id: string, data: UpdateReleaseInput) => {
    const res = await apiClient.patch<ApiResponse<ApiRelease>>(
      byId(API_ENDPOINTS.RELEASES, id),
      data,
    );
    return transformResponse(res);
  },
  removeRelease: async (id: string) => {
    const res = await apiClient.delete<ApiResponse<Message>>(
      byId(API_ENDPOINTS.RELEASES, id),
    );
    return transformResponse(res);
  },

  // --- events (shows) ---
  createEvent: async (data: CreateEventInput) => {
    const res = await apiClient.post<ApiResponse<ApiEvent>>(
      API_ENDPOINTS.EVENTS,
      data,
    );
    return transformResponse(res);
  },
  updateEvent: async (id: string, data: UpdateEventInput) => {
    const res = await apiClient.patch<ApiResponse<ApiEvent>>(
      byId(API_ENDPOINTS.EVENTS, id),
      data,
    );
    return transformResponse(res);
  },
  removeEvent: async (id: string) => {
    const res = await apiClient.delete<ApiResponse<Message>>(
      byId(API_ENDPOINTS.EVENTS, id),
    );
    return transformResponse(res);
  },

  // --- links (socials + platforms) ---
  createLink: async (data: CreateLinkInput) => {
    const res = await apiClient.post<ApiResponse<ApiLink>>(
      API_ENDPOINTS.LINKS,
      data,
    );
    return transformResponse(res);
  },
  updateLink: async (id: string, data: UpdateLinkInput) => {
    const res = await apiClient.patch<ApiResponse<ApiLink>>(
      byId(API_ENDPOINTS.LINKS, id),
      data,
    );
    return transformResponse(res);
  },
  removeLink: async (id: string) => {
    const res = await apiClient.delete<ApiResponse<Message>>(
      byId(API_ENDPOINTS.LINKS, id),
    );
    return transformResponse(res);
  },

  // --- contacts ---
  createContact: async (data: CreateContactInput) => {
    const res = await apiClient.post<ApiResponse<ApiContact>>(
      API_ENDPOINTS.CONTACTS,
      data,
    );
    return transformResponse(res);
  },
  updateContact: async (id: string, data: UpdateContactInput) => {
    const res = await apiClient.patch<ApiResponse<ApiContact>>(
      byId(API_ENDPOINTS.CONTACTS, id),
      data,
    );
    return transformResponse(res);
  },
  removeContact: async (id: string) => {
    const res = await apiClient.delete<ApiResponse<Message>>(
      byId(API_ENDPOINTS.CONTACTS, id),
    );
    return transformResponse(res);
  },

  // --- feedback ---
  submitFeedback: async (data: CreateFeedbackInput) => {
    const res = await apiClient.post<ApiResponse<Message>>(
      API_ENDPOINTS.FEEDBACK,
      data,
    );
    return transformResponse(res);
  },
};
